import { Link, useLocation, useNavigate } from "react-router-dom";
import { useState, useEffect, useRef } from "react";
import {
  LayoutDashboard, FileText, Briefcase, Image as ImageIcon, Settings as SettingsIcon,
  Newspaper, Inbox, LogOut, ExternalLink, Users, UserPlus, Search,
  MessageSquare, HelpCircle, UserCircle2, Award, BarChart3, Factory, Megaphone,
  Mail, ArrowRightLeft, Tag, Building2, MapPin, FileDown, Activity as ActivityIcon, Columns,
  BadgeCheck, KeyRound,
} from "lucide-react";
import { adminLogout } from "@/lib/adminApi";
import { globalSearch } from "@/lib/adminCmsApi";
import { AGS_LOGO_URL } from "@/lib/brand";

const NAV_CORE = [
  { to: "/admin", label: "Dashboard", icon: LayoutDashboard, exact: true },
  { to: "/admin/leads", label: "Leads", icon: Inbox },
  { to: "/admin/pipeline", label: "Pipeline", icon: Columns },
  { to: "/admin/pages", label: "Pages", icon: FileText },
  { to: "/admin/services", label: "Services", icon: Briefcase },
  { to: "/admin/blog", label: "Blog", icon: Newspaper },
  { to: "/admin/hiring", label: "Hiring", icon: UserPlus },
];

const NAV_CONTENT = [
  { to: "/admin/collections/testimonials", label: "Testimonials", icon: MessageSquare },
  { to: "/admin/collections/faqs", label: "FAQ", icon: HelpCircle },
  { to: "/admin/collections/team", label: "Team", icon: UserCircle2 },
  { to: "/admin/collections/case-studies", label: "Case Studies", icon: Award },
  { to: "/admin/collections/kpis", label: "KPIs", icon: BarChart3 },
  { to: "/admin/collections/sectors", label: "Sectors", icon: Factory },
  { to: "/admin/collections/banners", label: "Banners", icon: Megaphone },
  { to: "/admin/collections/partners", label: "Partners", icon: Building2 },
  { to: "/admin/collections/pricing-plans", label: "Pricing", icon: Tag },
  { to: "/admin/collections/locations", label: "Locations", icon: MapPin },
  { to: "/admin/collections/documents", label: "Documents", icon: FileDown },
  { to: "/admin/collections/trust-badges", label: "Trust Badges", icon: BadgeCheck },
];

const NAV_SYSTEM = [
  { to: "/admin/collections/subscribers", label: "Subscribers", icon: Mail },
  { to: "/admin/collections/redirects", label: "Redirects", icon: ArrowRightLeft },
  { to: "/admin/site-images", label: "Site Images", icon: ImageIcon },
  { to: "/admin/media", label: "Media Library", icon: ImageIcon },
  { to: "/admin/activity", label: "Activity Log", icon: ActivityIcon },
  { to: "/admin/users", label: "Users", icon: Users },
  { to: "/admin/integrations", label: "Integrations", icon: KeyRound },
  { to: "/admin/settings", label: "Settings", icon: SettingsIcon },
];

export default function AdminShell({ children, title }) {
  const { pathname } = useLocation();
  const isActive = (item) => item.exact ? pathname === item.to : pathname.startsWith(item.to);

  const renderGroup = (items, label) => (
    <div className="mb-3">
      {label && (
        <div className="px-3 pt-3 pb-1.5 font-mono text-[9px] uppercase tracking-[0.24em] text-white/25 border-t border-white/5 mt-2">
          {label}
        </div>
      )}
      {items.map((it) => {
        const Icon = it.icon;
        const active = isActive(it);
        return (
          <Link
            key={it.to}
            to={it.to}
            data-testid={`nav-${it.label.toLowerCase().replace(/\s+/g, "-")}`}
            className={`relative flex items-center gap-3 px-3 py-2 font-mono text-[11px] uppercase tracking-[0.18em] transition-colors duration-150 ${
              active
                ? "bg-[#0E2A57] text-white before:absolute before:left-0 before:top-0 before:bottom-0 before:w-[2px] before:bg-[#2BB6E5]"
                : "text-white/55 hover:text-white hover:bg-white/[0.04]"
            }`}
          >
            <Icon size={13} strokeWidth={1.75} />
            {it.label}
          </Link>
        );
      })}
    </div>
  );

  return (
    <div className="min-h-screen bg-ags-cream text-ags-ink flex admin-canvas">
      {/* Sidebar — AAA dark rail */}
      <aside className="w-64 bg-[#0A0A0A] text-white flex flex-col sticky top-0 h-screen border-r border-[#1F1F1F]">
        <Link to="/" data-testid="admin-brand-home" title="Go to homepage" className="px-6 py-5 border-b border-[#1F1F1F] block hover:bg-white/[0.03] transition-colors">
          <img src={AGS_LOGO_URL} alt="AGS Services — homepage" className="h-8 w-auto brightness-0 invert" />
          <div className="mt-3 font-mono text-[9px] uppercase tracking-[0.22em] text-[#2BB6E5]">CMS Console</div>
        </Link>
        <nav className="flex-1 px-2 py-3 overflow-y-auto">
          {renderGroup(NAV_CORE)}
          {renderGroup(NAV_CONTENT, "Content blocks")}
          {renderGroup(NAV_SYSTEM, "System")}
        </nav>
        <div className="px-2 py-3 border-t border-[#1F1F1F] space-y-0.5">
          <a
            href="/"
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center gap-3 px-3 py-2 font-mono text-[11px] uppercase tracking-[0.18em] text-white/55 hover:text-white hover:bg-white/[0.04] transition-colors"
          >
            <ExternalLink size={13} strokeWidth={1.75} /> View site
          </a>
          <button
            onClick={async () => { await adminLogout(); window.location.href = "/admin/login"; }}
            data-testid="admin-logout"
            className="w-full flex items-center gap-3 px-3 py-2 font-mono text-[11px] uppercase tracking-[0.18em] text-white/55 hover:text-white hover:bg-white/[0.04] transition-colors"
          >
            <LogOut size={13} strokeWidth={1.75} /> Sign out
          </button>
        </div>
      </aside>

      {/* Main */}
      <main className="flex-1 min-w-0">
        <header className="bg-white border-b border-ags-hairline px-6 py-4 flex items-center justify-between gap-6 sticky top-0 z-20 shadow-ags-ambient">
          <h1 className="font-heading font-extrabold text-lg tracking-[-0.02em] text-ags-ink truncate">{title}</h1>
          <GlobalSearchBar />
        </header>
        <div className="px-6 py-6 tabular-nums">{children}</div>
      </main>
    </div>
  );
}

function GlobalSearchBar() {
  const [q, setQ] = useState("");
  const [results, setResults] = useState([]);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const inputRef = useRef(null);

  useEffect(() => {
    if (q.trim().length < 2) {
      setResults([]);
      setOpen(false);
      return;
    }
    setLoading(true);
    const t = setTimeout(async () => {
      try {
        const data = await globalSearch(q.trim());
        setResults(data?.results || []);
        setOpen(true);
      } catch {
        setResults([]);
      } finally {
        setLoading(false);
      }
    }, 250);
    return () => clearTimeout(t);
  }, [q]);

  const linkFor = (r) => {
    switch (r.kind) {
      case "page": return `/admin/pages/${r.id}`;
      case "service": return `/admin/services`;
      case "blog": return `/admin/blog/${r.id}`;
      case "job": return `/admin/hiring`;
      case "testimonial": return `/admin/collections/testimonials`;
      case "faq": return `/admin/collections/faqs`;
      case "team": return `/admin/collections/team`;
      case "case-study": return `/admin/collections/case-studies`;
      case "kpi": return `/admin/collections/kpis`;
      case "sector": return `/admin/collections/sectors`;
      case "banner": return `/admin/collections/banners`;
      case "partner": return `/admin/collections/partners`;
      case "location": return `/admin/collections/locations`;
      case "document": return `/admin/collections/documents`;
      case "pricing": return `/admin/collections/pricing-plans`;
      default: return "/admin";
    }
  };

  return (
    <div className="relative w-full max-w-md">
      <div className="relative">
        <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#0E2A57]/40" />
        <input
          ref={inputRef}
          data-testid="global-search-input"
          type="search"
          placeholder="Search everything…"
          value={q}
          onChange={(e) => setQ(e.target.value)}
          onFocus={() => results.length && setOpen(true)}
          onBlur={() => setTimeout(() => setOpen(false), 150)}
          className="w-full bg-ags-cream border border-ags-hairline focus:border-[#0E2A57] focus:outline-none pl-9 pr-4 py-2 text-sm tabular-nums transition-colors"
        />
      </div>
      {open && (results.length > 0 || loading) && (
        <div className="absolute right-0 left-0 top-full mt-2 bg-white border border-ags-hairline-strong shadow-ags-hover z-30 max-h-96 overflow-y-auto">
          {loading && (
            <div className="px-4 py-3 text-xs text-ags-text-muted font-mono uppercase tracking-[0.18em]">Searching…</div>
          )}
          {!loading && results.length === 0 && (
            <div className="px-4 py-3 text-sm text-ags-text-muted">No results for &quot;{q}&quot;</div>
          )}
          {results.map((r, i) => (
            <button
              key={i}
              data-testid={`global-search-result-${i}`}
              onClick={() => { navigate(linkFor(r)); setOpen(false); setQ(""); }}
              className="w-full text-left px-4 py-2.5 hover:bg-ags-sand-soft border-b border-ags-hairline last:border-b-0 flex items-center gap-3 transition-colors"
            >
              <span className="font-mono text-[9px] uppercase tracking-[0.22em] text-[#0E2A57]/55 w-20 flex-shrink-0">{r.kind}</span>
              <span className="text-sm text-ags-ink truncate flex-1">{r.title}</span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
